<template>
    <div v-if="this.$root.user && this.$root.user.active || this.isNotProduction()" class="position-fixed bottom-0 end-0 p-3 d-flex justify-content-end chat-container" :class="{ 'full-screen': fullScreen }" style="z-index: 1050; width:auto;">        
        <!-- Chat button to toggle chat window -->
        <button 
            v-if="!isOpen" 
            class="btn btn-primary rounded-circle shadow" 
            @click="toggleChat"
            style="width: 60px; height: 60px;"
        >
            <font-awesome-icon icon="robot" class="chat-icon" />
        </button>

        <!-- Chat window -->
        <div v-if="isOpen" class="chat-window bg-white shadow p-0 m-0 d-flex flex-column" :class="{ 'full-screen': fullScreen }">
            <!-- Chat header -->
            <div class="chat-header p-3 row">
                <div class="col-3">
                    <button type="button" class="btn p-1 me-2" @click="toggleFullScreen()" 
                           data-bs-toggle="tooltip" data-bs-placement="bottom" title="Schermo intero">
                        <font-awesome-icon icon="expand" />
                    </button>
                    <button type="button" class="btn p-1" @click="this.recycleConfirm = true"
                           data-bs-toggle="tooltip" data-bs-placement="bottom" title="Nuova conversazione">
                        <font-awesome-icon icon="plus" />
                    </button>
                </div>
                <div class="col-6 text-center my-auto d-flex flex-column align-items-center gap-1">
                    <span id="chatbot-advice" class="badge bg-light text-secondary rounded-pill text-center mx-auto" 
                          style="border: 1px solid rgba(0,0,0,0.1); box-shadow: 0 1px 2px rgba(0,0,0,0.05);"
                          data-bs-toggle="advice-tooltip" 
                          data-bs-placement="top" 
                          title="Questo è un chatbot sperimentale. Le risposte potrebbero essere imprecise o incomplete. Usalo con cautela e verifica sempre le informazioni importanti da altre fonti.">
                        <font-awesome-icon icon="triangle-exclamation" class="me-1" />
                        <span style="font-weight: 500; letter-spacing: 0.3px;">Chatta responsabilmente</span>
                    </span>
                    <RouterLink to="/politica-utilizzo" class="badge bg-light text-primary rounded-pill text-decoration-none" style="border: 1px solid rgba(0,0,0,0.1); font-size: 0.7rem;" @click="toggleChat">
                        Politica di utilizzo AI4RUP
                    </RouterLink>
                </div>
                <div class="col-3 text-end">
                    <button class="btn p-1" @click="toggleChat"
                           data-bs-toggle="tooltip" data-bs-placement="bottom" title="Chiudi chat">
                        <font-awesome-icon icon="xmark" />
                    </button>
                </div>
            </div>

            <!-- Chat messages -->
            <div ref="messagesContainer" class="chat-messages p-3 flex-grow-1 overflow-auto">
                <div v-for="(message, index) in messages" :key="index" class="mb-3">
                    <!-- User message -->
                    <div v-if="message.isUser" class="d-flex justify-content-end">
                        <div class="message user-message p-2 rounded">
                            {{ message.text }}
                        </div>
                    </div>
                    <!-- Bot message -->
                    <div v-else class="d-flex flex-column justify-content-start">
                        <div class="message bot-message p-2 rounded" :class="{ 'is-error': message.isError }">
                            <div v-html="sanitize(message.text)"></div>
                            <div v-if="index === 0 && this.tips && this.tips.data && this.tips.data.length > 0">
                                <div class="col text-left">
                                    <ul>
                                        <li v-for="tip in this.tips.data" :key="tip.id" class="me-2 my-1">
                                            <a type="button" class="px-2 py-1" @click="selectTip(tip)">{{tip.title}}</a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div v-if="index !== 0 && ! message?.isError" class="d-flex justify-content-start">
                            <button
                                class="btn btn-sm like-button" 
                                title="Risposta giusta" 
                                @click="likeMessage(message)" 
                                :class="{ 'liked' : message.liked }"
                            >
                                <font-awesome-icon icon="thumbs-up" />
                            </button>
                            <button
                                class="btn btn-sm like-button" 
                                title="Risposta sbagliata" 
                                @click="dislikeMessage(message)" 
                                :class="{ 'disliked' : message.disliked }"
                            >
                                <font-awesome-icon icon="thumbs-down" />
                            </button>
                        </div>
                        <div v-if="message.failedMessage">
                            <button class="btn btn-sm text-muted p-1" type="button" @click="retryMessage(message)" title="Ritenta Trasmissione">
                                <font-awesome-icon icon="rotate-right" />
                            </button>
                        </div>
                        <div v-if="message.disliked" class="mt-2 p-3 feedback-box rounded" :class="{ 'closed-feedback': message.isFeedbackClosed, 'sended-feedback': message.feedback?.sent }">
                            <div v-if="!message.isFeedbackClosed">
                                <div class="d-flex justify-content-between align-items-center mb-2">
                                    <label class="mb-0 feedback-label">Fornisci altri dettagli:</label>
                                    <button class="btn btn-sm p-0" @click="toggleFeedback(message)" title="Chiudi">
                                        <font-awesome-icon icon="chevron-up" />
                                    </button>
                                </div>
                                <div class="d-flex">
                                    <div class="flex-grow-1">
                                        <textarea 
                                            v-model="message.feedbackText" 
                                            class="form-control me-2 feedback-textarea" 
                                            placeholder="Descrivi perché la risposta non è corretta..."
                                            rows="2"
                                        ></textarea>
                                        <transition name="fade-slide">
                                            <small v-if="message.feedback?.sent" class="text-muted feedback-thanks">Grazie per il tuo feedback</small>
                                        </transition>
                                    </div>
                                    <button 
                                        class="btn send-button align-self-end my-auto" 
                                        @click="sendFeedback(message, true)"
                                        data-bs-toggle="tooltip" data-bs-placement="top" 
                                        :title="message.feedback?.sent ? 'Modifica feedback' : 'Invia feedback'"
                                    >
                                        <font-awesome-icon :icon="message.feedback?.sent ? 'pen' : 'paper-plane'" />
                                    </button>
                                </div>
                            </div>
                            <div v-else>
                                <button class="btn btn-sm p-0" @click="toggleFeedback(message)" title="Chiudi">
                                    <font-awesome-icon icon="chevron-down" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Typing indicator -->
                <div v-if="isLoading" class="d-flex justify-content-start mb-3">
                    <div class="message bot-message p-2 rounded">
                        <div class="typing-indicator">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Chat input -->
            <div class="chat-input p-3 border-top">
                <form @submit.prevent="sendMessage" class="d-flex">
                    <input 
                        type="text" 
                        v-model="newMessage" 
                        class="form-control me-2" 
                        placeholder="Scrivi un messaggio..."
                        :disabled="isLoading"
                    >
                    <button 
                        type="submit" 
                        class="btn p-1 send-button" 
                        :disabled="!newMessage.trim() || isLoading"
                        data-bs-toggle="tooltip" data-bs-placement="left" 
                        title="Invia messaggio"
                    >
                        <font-awesome-icon icon="fa-circle-up" style="width: 22px; height: 22px;"/>
                    </button>
                </form>
            </div>
        </div>
        
        <!-- Recycle confirmation modal -->
        <div v-if="recycleConfirm" class="modal fade show d-block" id="recycleModal" tabindex="-1" role="dialog" aria-labelledby="recycleModalLabel" aria-hidden="true">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <svg class="icon me-2" aria-hidden="true">
                            <use :href='this.$root.baseURL + "/bootstrap-italia/dist/svg/sprites.svg#it-info-circle"'></use>
                        </svg>
                        <h5 class="modal-title" id="recycleModalLabel">Crea nuova chat</h5>
                        <button type="button" class="btn btn-close" @click="recycleConfirm = false" aria-label="Close"/>
                    </div>
                    <div class="modal-body">
                        <p>Sei sicuro di voler creare una nuova conversazione?</p>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="recycleConfirm = false">Annulla</button>
                        <button type="button" class="btn btn-primary" @click="recycleChat">Crea nuova chat</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { sanitize } from '@/utils/sanitize';
import { marked } from 'marked';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faThumbsUp, faThumbsDown, faPaperPlane, faTimes, faRotateRight, faPen, faChevronUp, faChevronDown, faExpand, faPlus, faTriangleExclamation, faXmark, faRobot, faCircleUp} from '@fortawesome/free-solid-svg-icons'
import * as bootstrap from 'bootstrap'
import utils from '@/mixins/utils'

// Aggiungi le icone alla libreria
library.add(faThumbsUp, faThumbsDown, faPaperPlane, faTimes, faRotateRight, faPen, faChevronUp, faChevronDown, faExpand, faPlus, faTriangleExclamation, faXmark, faRobot, faCircleUp)

export default {
    name: 'ChatBot',
    mixins: [utils],
    components: {
        FontAwesomeIcon
    },
    data() {
        return {
            isOpen: false,
            fullScreen: false,
            messages: [],
            tips: [],
            newMessage: '',
            isLoading: false,
            currentStreamingMessage: '',
            currentConversationId: null,
            abortController: null,
            likedMessages: [],
            recycleConfirm: false
        }
    },
    created() {
        // Carica lo stato della chat dal localStorage
        this.loadChatState();
        this.loadTips();
        
        // Aggiungi un event listener per salvare lo stato prima di chiudere la pagina
        window.addEventListener('beforeunload', this.saveChatState);
    },
    mounted() {
        // Inizializza i tooltip di Bootstrap Italia
        this.$nextTick(() => {
            const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
            tooltipTriggerList.map(function (tooltipTriggerEl) {
                return new bootstrap.Tooltip(tooltipTriggerEl);
            });
        });
    },
    beforeUnmount() {
        // Rimuovi l'event listener quando il componente viene smontato
        window.removeEventListener('beforeunload', this.saveChatState);
        
        // Salva lo stato della chat
        this.saveChatState();
    },
    methods: {
        // Metodo per convertire Markdown in HTML
        convertMarkdownToHtml(markdownText) {
            if (!markdownText) return '';
            try {
                return marked.parse(markdownText);
            } catch (error) {
                console.error('Errore nella conversione da Markdown a HTML:', error);
                return markdownText; // Ritorna il testo originale in caso di errore
            }
        },

        toggleFeedback(message) {
            message.isFeedbackClosed = !message.isFeedbackClosed;
        },
        
        createFirstMessage() {
            let message = "Ciao! Sono AI4Rup, l'assistente digitale del Codice dei Contratti Pubblici, posso aiutarti a trovare le informazioni che stai cercando";
            if (this.tips.length > 0) {
                message += '\n\nProva con uno di questi suggerimenti:'
            }
            this.messages.push({
                text: this.convertMarkdownToHtml(message),
                isUser: false
            });
        },
        
        toggleChat() {
            this.isOpen = !this.isOpen;
            
            // Se chiudiamo la chat, disattiviamo anche la modalità fullscreen
            if (!this.isOpen) {
                this.fullScreen = false;
            }
            
            this.$nextTick(() => {
                if (this.isOpen) {
                    this.scrollToBottom();
                }
            });
            
            if (this.isOpen && this.messages.length === 0) {
                this.createFirstMessage();
            }
            this.saveChatState();
        },
        toggleFullScreen() {
            this.fullScreen = !this.fullScreen;
            this.saveChatState();
        },
        async sendMessage() {
            this.scrollToBottom();
            if (!this.newMessage.trim() || this.isLoading) return;
            
            const userMessage = this.newMessage.trim();
            this.messages.push({
                text: userMessage,
                isUser: true
            });
            
            this.newMessage = '';
            this.isLoading = true;
            this.currentStreamingMessage = '';
            
            // Add bot message placeholder for streaming response
            this.messages.push({
                text: '',
                isUser: false
            });
            
            const messageToSend = this.constructMessage(userMessage);
            try {
                // Create an AbortController to cancel the fetch if needed
                this.abortController = new AbortController();
                const signal = this.abortController.signal;
                
                // Ottieni il token dal localStorage
                const token = localStorage.getItem('token');
                
                const response = await fetch(`${this.$root.config.VUE_APP_BACKOFFICE_URL}/test-ask`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'text/event-stream',
                        'Authorization': token ? `Bearer ${token}` : ''
                    },
                    body: JSON.stringify(messageToSend),
                    signal
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                
                // eslint-disable-next-line no-constant-condition
                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    
                    buffer += decoder.decode(value, { stream: true });
                    
                    // Process complete data events
                    const lines = buffer.split('\n');
                    buffer = lines.pop(); // Keep the last incomplete line in the buffer
                    
                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            const data = line.substring(6); // Remove 'data: ' prefix
                            try {
                                const parsedData = JSON.parse(data);
                                
                                // Handle first message with conversation details
                                if (parsedData.first) {
                                    this.currentConversationId = parsedData.conversation_id;
                                }
                                // Handle text chunks
                                else if (parsedData.text !== undefined) {
                                    this.currentStreamingMessage += parsedData.text;
                                    // Update the last message in the array (bot message) with HTML convertito da Markdown
                                    this.messages[this.messages.length - 1].text = this.convertMarkdownToHtml(this.currentStreamingMessage);
                                }
                                // Handle final message
                                else if (parsedData.final) {
                                    // Store conversation details if needed
                                    if (parsedData.conversation) {
                                        this.currentConversationId = parsedData.conversation.id;
                                        this.currentStreamingMessage = parsedData.responseMessage.text;
                                        // Converti il messaggio finale da Markdown a HTML
                                        this.messages[this.messages.length - 1].text = this.convertMarkdownToHtml(this.currentStreamingMessage);
                                        this.messages[this.messages.length - 1].conversationId = parsedData.responseMessage.conversation_id;
                                        this.messages[this.messages.length - 1].id = parsedData.responseMessage.id;
                                    }
                                    // Final message text is already in the messages array
                                    break;
                                }
                            } catch (e) {
                                console.error('Error parsing JSON:', e, data);
                            }
                        }
                    }
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Error fetching response:', error);
                    // Update the last message to show the error
                    this.messages[this.messages.length - 1].text = 'Mi dispiace, un errore ha impedito la gestione della richiesta.';
                    this.messages[this.messages.length - 1].isError = true;
                    this.messages[this.messages.length - 1].failedMessage = messageToSend
                    this.saveChatState();
                }
            } finally {
                this.isLoading = false;
                this.abortController = null;
                // Salva lo stato dopo ogni messaggio inviato/ricevuto
                this.saveChatState();
            }
        },
        async retryMessage(message) {
            let userMessage = message.failedMessage.text;
            
            this.newMessage = '';
            this.isLoading = true;
            this.currentStreamingMessage = '';
            
            this.messages.push({
                text: '',
                isUser: false
            });

            
            let messageToSend = this.constructMessage(userMessage);
            messageToSend.override_message_id = message.id;
            try {
                // Create an AbortController to cancel the fetch if needed
                this.abortController = new AbortController();
                const signal = this.abortController.signal;
                
                // Ottieni il token dal localStorage
                const token = localStorage.getItem('token');
                
                const response = await fetch(`${this.$root.config.VUE_APP_BACKOFFICE_URL}/test-ask`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'text/event-stream',
                        'Authorization': token ? `Bearer ${token}` : ''
                    },
                    body: JSON.stringify(messageToSend),
                    signal
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                
                // eslint-disable-next-line no-constant-condition
                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    
                    buffer += decoder.decode(value, { stream: true });
                    
                    // Process complete data events
                    const lines = buffer.split('\n');
                    buffer = lines.pop(); // Keep the last incomplete line in the buffer
                    
                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            const data = line.substring(6); // Remove 'data: ' prefix
                            try {
                                const parsedData = JSON.parse(data);
                                
                                // Handle first message with conversation details
                                if (parsedData.first) {
                                    this.currentConversationId = parsedData.conversation_id;
                                }
                                // Handle text chunks
                                else if (parsedData.text !== undefined) {
                                    this.currentStreamingMessage += parsedData.text;
                                    // Update the last message in the array (bot message) with HTML convertito da Markdown
                                    this.messages[this.messages.length - 1].text = this.convertMarkdownToHtml(this.currentStreamingMessage);
                                }
                                // Handle final message
                                else if (parsedData.final) {
                                    // Store conversation details if needed
                                    if (parsedData.conversation) {
                                        this.currentConversationId = parsedData.conversation.id;
                                        this.currentStreamingMessage = parsedData.responseMessage.text;
                                        // Converti il messaggio finale da Markdown a HTML
                                        this.messages[this.messages.length - 1].text = this.convertMarkdownToHtml(this.currentStreamingMessage);
                                        this.messages[this.messages.length - 1].conversationId = parsedData.responseMessage.conversation_id;
                                        this.messages[this.messages.length - 1].id = parsedData.responseMessage.id;
                                    }
                                    // Final message text is already in the messages array
                                    break;
                                }
                            } catch (e) {
                                console.error('Error parsing JSON:', e, data);
                            }
                        }
                    }
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Error fetching response:', error);
                    // Update the last message to show the error
                    this.messages[this.messages.length - 1].text = 'Mi dispiace, un errore ha impedito la gestione della richiesta.';
                    this.messages[this.messages.length - 1].isError = true;
                    this.messages[this.messages.length - 1].failedMessage = messageToSend
                    this.saveChatState();
                }
            } finally {
                this.isLoading = false;
                this.abortController = null;
                // Salva lo stato dopo ogni messaggio inviato/ricevuto
                this.saveChatState();
            }
        },
        scrollToBottom() {
            this.$nextTick(() => {
                if (this.$refs.messagesContainer) {
                    this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
                }
            });
        },
        constructMessage(userMessage) {
            let object = {}
            object["text"] = userMessage;
            if (this.currentConversationId) {
                object["conversation_id"] = this.currentConversationId;
            }
            return object
        },
        formatMessage(text) {
            // Non necessario poiché usiamo marked per la conversione Markdown
            return text;
        },
        likeMessage(message) {
            message.feedback = {}
            message.disliked = false;
            message.liked = true;
            message.feedbackStatus = 'positive';
            message.feedbackText = '';
            this.sendFeedback(message);
            this.saveChatState();
        },
        dislikeMessage(message) {
            message.feedback = {}
            message.liked = false;
            message.disliked = true;
            // Inizializza l'oggetto feedback
            message.feedbackStatus = 'negative';
            message.feedbackText = '';
            // Salva lo stato dopo ogni feedback
            this.sendFeedback(message);
            this.saveChatState();
        },
        closeFeedback(message) {
            if (message.feedback) {
                message.feedback.closed = true;
            } else {
                message.feedback = { closed: true };
            }
            this.saveChatState();
        },
        async selectTip(tip) {
            this.newMessage = tip.message;
            this.sendMessage();
        },
        async sendFeedback(message, andClose = false) {
            message.feedback.text = message.feedbackText;


            // Aggiorna lo stato del feedback
            if (!message.feedback) {
                message.feedback = {};
            }

            try {
                // Ottieni il token dal localStorage
                const token = localStorage.getItem('token');
                
                const response = await fetch(`${this.$root.config.VUE_APP_BACKOFFICE_URL}/message-feedback`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': token ? `Bearer ${token}` : ''
                    },
                    body: JSON.stringify(message),
                });
            } catch (error) {
                console.error('Error sending feedback:', error);
            }
            
            if (andClose) {
                message.feedback.sent = true;
                message.feedback.closed = true;
                message.feedback.timestamp = new Date().toISOString();
            }
            
            this.saveChatState();
        },
        saveChatState() {
            // Salva lo stato della chat nel localStorage
            const chatState = {
                isOpen: this.isOpen,
                messages: this.messages,
                fullScreen: this.fullScreen,
                currentConversationId: this.currentConversationId,
                timestamp: new Date().getTime()
            };
            localStorage.setItem('chatState', JSON.stringify(chatState));
        },
        async loadTips() {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${this.$root.config.VUE_APP_BACKOFFICE_URL}/get-chat-tips`, { headers: {'Authorization': token ? `Bearer ${token}` : ''} });
                const data = await response.json();
                this.tips = data;
            } catch (error) {
                console.error('Error loading tips:', error);
            }
        },
        loadChatState() {
            try {
                // Carica lo stato della chat dal localStorage
                const savedState = localStorage.getItem('chatState');
                if (savedState) {
                    const chatState = JSON.parse(savedState);
                    
                    // Verifica se lo stato salvato è ancora valido (meno di 24 ore)
                    const now = new Date().getTime();
                    const savedTime = chatState.timestamp || 0;
                    const oneDay = 24 * 60 * 60 * 1000; // 24 ore in millisecondi
                    
                    if (now - savedTime < oneDay) {
                        // Ripristina lo stato
                        this.isOpen = chatState.isOpen;
                        this.messages = chatState.messages || [];
                        this.fullScreen = chatState.fullScreen || false;
                        this.currentConversationId = chatState.currentConversationId;
                    } else {
                        // Lo stato è troppo vecchio, lo resettiamo
                        localStorage.removeItem('chatState');
                    }
                }
            } catch (error) {
                console.error('Errore nel caricamento dello stato della chat:', error);
                // In caso di errore, resetta lo stato
                localStorage.removeItem('chatState');
            }
        },
        recycleChat() {
            this.messages = [];
            this.currentConversationId = null;
            this.createFirstMessage();
            this.recycleConfirm = false;
        },
        sanitize(html) {
            return sanitize(html);
        }
    }
}
</script>

<style scoped>

.chat-container {
    max-width:95vw;
}

.closed-feedback {
    max-width: 50px !important;
}

.chat-window {
    width: 420px;
    height: 600px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    border-radius: 18px;
    border-bottom-right-radius: 0px;
}

.full-screen {
    width: 100% !important;
    height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
}

.chat-messages {
    height: 100%;
    overflow-y: auto;
}

.message {
    max-width: 90%;
    word-break: break-word;
}

.user-message {
    background-color: #0066cc;
    color: #fff;
    border-radius: 18px 18px 0 18px !important;
}

.bot-message {
    background-color: #f1f1f1;
    color: #333;
    border-radius: 18px 18px 18px 0 !important;
}

.bot-message > p {
    margin-bottom:0 !important;
}

/* Typing indicator animation */
.typing-indicator {
    display: flex;
    align-items: center;
}

.typing-indicator span {
    height: 8px;
    width: 8px;
    background-color: #888;
    border-radius: 50%;
    display: inline-block;
    margin: 0 2px;
    animation: bounce 1.5s infinite ease-in-out;
}

.typing-indicator span:nth-child(2) {
    animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
    animation-delay: 0.4s;
}

.is-error {
    background-color:rgb(252, 216, 216);
    color: #333;
}

.send-button {
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease;
    width: 40px;
    height: 40px;
    border-radius: 50%;
}

.send-button:disabled > svg {
    color:#5d7083;
}

.send-button:hover:not(:disabled) {
    transform: scale(1.1);
}

.send-button:active:not(:disabled) {
    transform: scale(0.95);
}

.feedback-textarea {
    border-radius: 18px;
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    resize: none;
    transition: all 0.2s ease;
    font-size: 0.9rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.feedback-textarea:focus {
    background-color: #fff;
    border-color: #dcf0ff;
    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.1);
}

.send-button svg {
    font-size: 18px;
    color: #0066cc;
}

.like-button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1em;
    width: 32px;
    height: 32px;
    transition: all 0.2s ease;
}

.like-button:hover {
    transform: scale(1.05);
}

.like-button svg {
    font-size: 16px;
    color: #0066cc;
}

.liked, .disliked {
    animation: pulse 0.4s ease-in-out;
}

.liked svg {
    color:rgb(30, 179, 0);
    transform: scale(1.2);
    transition: transform 0.2s ease;
}

.disliked svg {
    color: #dc3545;
    transform: scale(1.2);
    transition: transform 0.2s ease;
}

.feedback-box {
    background-color: #f8f9fa;
    width: 100%;
    max-width: 350px;
    margin-top: 0.5rem;
    transition: background-color 0.5s ease-in-out;
}

.sended-feedback {
    background-color:rgb(225, 245, 223) !important;
    animation: feedback-success-pulse 1s ease-in-out;
}

.sended-feedback .feedback-textarea {
    background-color:rgb(193, 225, 190) !important;
    transition: background-color 0.5s ease-in-out;
}

.sended-feedback .send-button > * {
    color:rgb(131, 177, 127) !important;
    transition: color 0.5s ease-in-out;
}

@keyframes feedback-success-pulse {
    0% { background-color: #f8f9fa; }
    50% { background-color: rgb(193, 225, 190); }
    100% { background-color: rgb(225, 245, 223); }
}

.feedback-thanks {
    display: block;
    margin-top: 5px;
    font-style: italic;
}

.fade-slide-enter-active {
    transition: all 0.5s ease;
}

.fade-slide-leave-active {
    transition: all 0.3s ease;
}

.fade-slide-enter-from, 
.fade-slide-leave-to {
    transform: translateY(-10px);
    opacity: 0;
}

.fade-slide-enter-to, 
.fade-slide-leave-from {
    transform: translateY(0);
    opacity: 1;
}

.feedback-label {
    font-size: 0.9rem;
    font-weight: 500;
    color: #495057;
}

.chat-icon {
    width:30px;
    height:30px;
    position:relative;
    left:-75%;
    fill:white;
}

.icon-sm {
    width:14px;
    height:14px;
}

.send-icon {
    width:24px;
    height:24px;
    fill:#0066cc;
}

#chatbot-advice {
    cursor: pointer;
}


@keyframes bounce {
    0%, 60%, 100% {
        transform: translateY(0);
    }
    30% {
        transform: translateY(-4px);
    }
}

@keyframes pulse {
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.05);
    }
    100% {
        transform: scale(1);
    }
}
</style>